import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/primitives';
import { PlusOutlined, LeftOutlined, RightOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  onAction?: () => void;
  action?: ReactNode;
  /** When provided, renders a back chevron with this label. Clicking navigates back (or to `backTo` if set). */
  back?: boolean | string;
  backTo?: string | number;
  eyebrow?: ReactNode;
}

export default function PageHeader({
  title, subtitle, actionLabel, onAction, action, back, backTo, eyebrow,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const ChevronBack = isRtl ? RightOutlined : LeftOutlined;
  const backLabel = typeof back === 'string' ? back : t('common.back');

  const handleBack = () => {
    if (backTo !== undefined) {
      if (typeof backTo === 'number') navigate(backTo);
      else navigate(backTo);
      return;
    }
    navigate(-1);
  };

  return (
    <div className="ds-page-header">
      <div>
        {back && (
          <button
            type="button"
            onClick={handleBack}
            className="ds-btn ds-btn--text"
            style={{
              padding: '4px 8px',
              height: 28,
              marginBottom: 6,
              marginInlineStart: -8,
              fontSize: 13,
              color: 'var(--text-secondary)',
            }}
          >
            <ChevronBack />
            <span>{backLabel}</span>
          </button>
        )}
        {eyebrow && (
          <div
            style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.08em',
              textTransform: 'uppercase', color: 'var(--text-tertiary)',
              marginBottom: 4,
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1>{title}</h1>
        {subtitle && <div className="ds-page-sub">{subtitle}</div>}
      </div>
      {action
        ? action
        : actionLabel && onAction && (
          <Button type="primary" icon={<PlusOutlined />} onClick={onAction}>
            {actionLabel}
          </Button>
        )}
    </div>
  );
}
